import { Clear } from '@mui/icons-material';
import DatePicker from '@mui/lab/DatePicker';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import FormatCurrencyInput from '../../components/FormatNumber/FormatCurrencyInput';
import FormatNumberInput from '../../components/FormatNumber/FormatNumberInput';
import { typingTimeOut } from '../../constants';
import { ProjectPaymentType } from '../../enums/projectPaymentType';
import { ProjectStatus } from '../../enums/projectStatus';
import { useUserRoles } from '../../hooks/useUserRoles';
import { Talent } from '../../models/talent';
import { talentService } from '../../services/talentService';

export interface FilterProjectsForm {
  startDate?: string | null;
  endDate?: string | null;
  projectStatuses?: string[];
  id?: string;
  projectTitle?: string;
  category?: string;
  paymentType?: string;
  budget?: string;
  amountOpen?: string;
  talentIds?: Talent[];
}

interface Props {
  handleFormSubmit: (data: FilterProjectsForm) => void;
  filterData: FilterProjectsForm;
}

const defaultValues: FilterProjectsForm = {
  startDate: null,
  endDate: null,
  projectStatuses: [],
  id: '',
  projectTitle: '',
  category: '',
  paymentType: '',
  budget: '',
  amountOpen: '',
  talentIds: []
};

const FilterProjects = (props: Props): JSX.Element => {
  const { isManager } = useUserRoles();
  const [filterData, setFilterData] = useState<FilterProjectsForm>(
    props.filterData
  );
  const [talents, setTalents] = useState<Talent[]>([]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      props.handleFormSubmit(filterData);
    }, typingTimeOut);

    return () => clearTimeout(timeoutId);
  }, [filterData]);

  useEffect(() => {
    isManager && getTalents();
  }, []);

  const getTalents = () => {
    talentService.getTalentList().then((response) => {
      setTalents(response.data);
    });
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFilterData((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value
    }));
  };

  const handleSelectChange = (
    event: SelectChangeEvent<string[]> | SelectChangeEvent
  ) => {
    const {
      target: { value, name }
    } = event;
    setFilterData((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const onClear = () => {
    setFilterData(defaultValues);
  };

  return (
    <form>
      <Grid container>
        <Grid item xs={11}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {isManager && (
              <>
                <Autocomplete
                  multiple
                  id="status"
                  options={Object.keys(ProjectStatus)}
                  getOptionLabel={(option) => {
                    const key = option as keyof typeof ProjectStatus;
                    return ProjectStatus[key];
                  }}
                  value={filterData.projectStatuses ?? []}
                  onChange={(event, newValue) => {
                    setFilterData((prevState) => ({
                      ...prevState,
                      projectStatuses: newValue
                    }));
                  }}
                  filterSelectedOptions
                  sx={{ width: '100%', maxWidth: 245 }}
                  renderInput={(params) => (
                    <TextField {...params} label="Status" />
                  )}
                />
              </>
            )}
            <TextField
              sx={{ maxWidth: 245 }}
              label="Project #"
              name="id"
              value={filterData.id}
              onChange={handleChange}
              InputProps={{
                // eslint-disable-next-line
                inputComponent: FormatNumberInput as any
              }}
            />
            <TextField
              sx={{ maxWidth: 245 }}
              label="Title"
              name="projectTitle"
              value={filterData.projectTitle}
              onChange={handleChange}
            />
            {isManager && (
              <TextField
                sx={{ maxWidth: 245 }}
                label="Category"
                name="category"
                value={filterData.category}
                onChange={handleChange}
              />
            )}
            <DatePicker
              label="Start Date"
              value={filterData.startDate}
              onChange={(date) => {
                setFilterData((prevState) => ({
                  ...prevState,
                  startDate: date
                }));
              }}
              maxDate={filterData.endDate}
              renderInput={(params) => (
                <TextField {...params} sx={{ maxWidth: 245 }} />
              )}
            />
            <DatePicker
              label="End Date"
              value={filterData.endDate}
              onChange={(date) => {
                setFilterData((prevState) => ({
                  ...prevState,
                  endDate: date
                }));
              }}
              minDate={filterData.startDate}
              renderInput={(params) => (
                <TextField {...params} sx={{ maxWidth: 245 }} />
              )}
            />
            <FormControl sx={{ width: '100%', maxWidth: 245 }}>
              <InputLabel id="paymentType">Payment Type</InputLabel>
              <Select
                labelId="paymentType"
                label="paymentType"
                name="paymentType"
                value={filterData.paymentType}
                onChange={handleSelectChange}
                input={<OutlinedInput label="Payment Type" />}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {Object.keys(ProjectPaymentType).map((paymentType) => (
                  <MenuItem key={paymentType} value={paymentType}>
                    {
                      ProjectPaymentType[
                        paymentType as keyof typeof ProjectPaymentType
                      ]
                    }
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              sx={{ maxWidth: 245 }}
              label="Budget"
              name="budget"
              value={filterData.budget}
              onChange={handleChange}
              InputProps={{
                // eslint-disable-next-line
                inputComponent: FormatCurrencyInput as any
              }}
            />
            <TextField
              sx={{ maxWidth: 245 }}
              label="Amount Open"
              name="amountOpen"
              value={filterData.amountOpen}
              onChange={handleChange}
              InputProps={{
                // eslint-disable-next-line
                inputComponent: FormatCurrencyInput as any
              }}
            />
            {isManager && (
              <Autocomplete
                multiple
                id="talent"
                options={talents}
                getOptionLabel={(option) =>
                  `${option.firstName} ${option.lastName}`
                }
                value={filterData.talentIds ?? []}
                onChange={(event, newValue) => {
                  setFilterData((prevState) => ({
                    ...prevState,
                    talentIds: newValue
                  }));
                }}
                filterSelectedOptions
                sx={{ width: '100%', maxWidth: 245 }}
                renderInput={(params) => (
                  <TextField {...params} label="Talent" />
                )}
              />
            )}
          </Box>
        </Grid>
        <Grid item xs={1}>
          <Button
            variant="text"
            startIcon={<Clear />}
            onClick={() => onClear()}
          >
            Clear
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default FilterProjects;
